export type Locale = 'en' | 'fr' | 'bm';

const en = {
  'nav.mission': 'Our Story',
  'nav.programs': 'Programs',
  'nav.learning': 'Learning',
  'nav.impact': 'Impact',
  'nav.where': 'Where we work',
  'nav.governance': 'Governance',
  'nav.community': 'Community',
  'nav.partners': 'Partners',
  'nav.news': 'News',
  'nav.involved': 'Get involved',
  'nav.donate': 'Donate',
  'nav.contact': 'Contact',
  'nav.home': 'Home',
  'nav.more': 'More',
  'nav.menu': 'Open menu',
  'nav.close': 'Close menu',
  'lang.en': 'EN',
  'lang.fr': 'FR',
  'lang.bm': 'BM',

  'doc.org': 'HEALTHGEEK INITIATIVE — 501(c)(3)',
  'brand.health': 'HEALTH',
  'brand.geek': 'GEEK',

  'hero.pill': 'Est. 2026 — Mali & United States',
  'hero.title.1': 'Two places,',
  'hero.title.em': 'one',
  'hero.title.2': 'network.',
  'hero.lede':
    'A small non-profit working in Mali and the United States, run by people who’d rather fix one thing well than announce a hundred.',
  'hero.quote':
    'We didn’t start HealthGeek to bring healthcare to anyone. We started it because a dentist in Bamako and a clinic on a reservation in Michigan are solving the same problem, alone, when they don’t have to be.',
  'hero.attr': '— Dr. Miah Ramsey, Chairwoman & Executive Director',
  'hero.cta.programs': 'See our programs',
  'hero.cta.charter': 'Read our story',
  'hero.illus.caption': 'Bamako & Upper Michigan — one network, two starting points.',
  'hero.stat.clinics': 'Partner clinics & community health centers',
  'hero.stat.countries': 'Countries — Mali and the United States — one network',
  'hero.stat.codesign': 'Devices refurbished and redeployed',
  'hero.video.kicker': 'From the field',
  'hero.video.title': 'Bamako and Upper Michigan, in the same room.',

  'impact.kicker': '01 — Where things stand',
  'impact.lede': 'Updated quarterly. Full detail lives in our Annual Impact & Learning Report.',
  'impact.title': 'By the numbers.',
  'impact.clinics.label': 'Partner clinics & community health centers',
  'impact.clinics.desc': 'Clinics and community health centers in the current network.',
  'impact.chws.label': 'Health workers trained through our programs',
  'impact.chws.desc': 'Clinicians, technicians, and community health workers trained to date.',
  'impact.regions.label': 'Countries — Mali and the United States — one network',
  'impact.regions.desc': 'Two starting points, one shared approach.',
  'impact.local.label': 'Devices refurbished and redeployed',
  'impact.local.desc': 'Hardware brought back into service through Juvo.',

  'pillars.kicker': 'Our Story',
  'pillars.title': 'Two people, one question.',
  'pillars.lede':
    'HealthGeek started as a conversation between a computer scientist and a practicing dentist about why the same broken supply chain kept showing up in two places that had never spoken to each other.',
  'pillars.1.n': 'RULE ONE',
  'pillars.1.title': 'We don’t design anything without the people who’ll use it.',
  'pillars.1.body':
    'Every intervention is co-created with local leaders. Communities decide what gets built next, and they keep what works after we’ve left the room.',
  'pillars.2.n': 'RULE TWO',
  'pillars.2.title': 'We’re not trying to be needed forever.',
  'pillars.2.body':
    'We measure ourselves on how much capacity we’ve handed off, not how much aid we’ve delivered. If a clinic doesn’t need us in five years, we did our job.',
  'pillars.3.n': 'VISION',
  'pillars.3.title': 'A world where geography doesn’t decide your health outcome.',
  'pillars.3.body':
    'From Mali to Michigan and wherever this leads next, every person should get care that actually works — delivered by professionals who are supported, not stretched thin.',
  'pillars.4.n': 'EXCHANGE',
  'pillars.4.title': 'Knowledge does not only flow one way.',
  'pillars.4.body':
    'There’s a lot the global south and north have to learn from each other. We build so that exchange can happen in both directions.',

  'programs.kicker': 'Programs',
  'programs.title': 'Three programs, one mission.',
  'programs.lede': 'Each one is run by the people closest to the work.',
  'programs.mali.tag': 'HealthGeek Mali',
  'programs.mali.title': 'Fix what’s here first.',
  'programs.mali.body':
    'The first project in Bamako wasn’t new equipment — it was a relationship with clinics that already had machines sitting broken in a back room. Everything since has followed the same rule: fix what’s here before shipping what’s new.',
  'programs.mali.teaser': 'Community-led clinical support, built around maintenance and training.',
  'programs.mali.link': 'Read more about our approach',
  'programs.juvo.tag': 'Juvo',
  'programs.juvo.title': 'Refurbish, train, redeploy.',
  'programs.juvo.body':
    'Juvo takes in donated and discarded medical hardware, and pays clinics back in two ways: fixed equipment, or credit toward a training program that teaches someone to fix the next one themselves.',
  'programs.juvo.teaser': 'A circular model turning donated hardware into working clinics.',
  'programs.juvo.link': 'Request equipment or training',
  'programs.di.tag': 'Dental Intelligence',
  'programs.di.title': 'The nearest specialist, in the room.',
  'programs.di.body':
    'Dental Intelligence reads an X-ray the moment it’s taken, flags what a trained eye might miss, and gets better every time a practitioner uses it — so the nearest expert is the tool in the room, not a four-hour drive away.',
  'programs.di.teaser': 'AI-assisted radiograph review and clinical training, built for rural practice.',
  'programs.di.link': 'Visit dental.healthgeek.org.ml',

  'programs.section': '02 — What we’re building',
  'programs.page.title': 'Three efforts, told the way the people running them would tell you.',
  'programs.mali.headline': 'Seb didn’t want another donated X-ray machine nobody could fix.',
  'programs.mali.who': 'Seb, Secretary of the Board & Director of Programs, Mali',
  'programs.mali.meta1': 'Bamako',
  'programs.mali.meta1l': 'Operational HQ',
  'programs.mali.meta2': 'Phase 1',
  'programs.mali.meta2l': 'West Africa expansion',
  'programs.juvo.headline': 'The tech nobody wanted became the training nobody could afford.',
  'programs.juvo.who': 'Founding team: a computer scientist and a practicing dentist',
  'programs.juvo.meta1': '30+',
  'programs.juvo.meta1l': 'Devices refurbished',
  'programs.juvo.meta2': '8',
  'programs.juvo.meta2l': 'Certificate programs offered',
  'programs.di.headline': 'A dental student in a village clinic shouldn’t need a specialist on speed-dial.',
  'programs.di.who': 'Developed with the National Dental Board of Mali and RobotsMali AI4D Lab',
  'programs.di.meta1': 'AI-assisted',
  'programs.di.meta1l': 'Radiographic review',
  'programs.di.meta2': 'Certified',
  'programs.di.meta2l': 'Skill-tracking & certification',

  'help.section': '03 — Ways to help',
  'help.title': 'There’s a place for you in this, whoever you are.',
  'help.lede': 'Not everyone can write a check, and not everyone wants to. Here’s what actually moves things forward.',
  'help.donate.title': 'Make a donation',
  'help.donate.body': 'A one-time gift goes straight to a clinic or a training cohort — you’ll know which.',
  'help.monthly.title': 'Give monthly',
  'help.monthly.body': 'Recurring support is what lets us plan a training calendar instead of chasing one.',
  'help.geek.title': 'Become a HealthGeek Geek',
  'help.geek.body': 'Pro-bono experts, fellows, and local volunteers who do the actual field work.',
  'help.partner.title': 'Partner with us',
  'help.partner.body': 'Health-tech companies, universities, and CSR teams — reach out and we’ll start a conversation.',

  'geo.title.1': 'One network,',
  'geo.title.em': 'two',
  'geo.title.2': 'starting points.',
  'geo.cap1': 'Bamako & Upper Michigan',
  'geo.cap2': 'One network, two starting points.',
  'geo.us.title': 'United States',
  'geo.us.body':
    'Native American communities and tribal health centers, beginning in Upper Michigan, plus other underserved rural clinics.',
  'geo.p1.title': 'Mali — operational HQ',
  'geo.p1.body': 'Community-led clinical support from Bamako, built around maintenance and training.',
  'geo.p2.title': 'West Africa, next',
  'geo.p2.body': 'We expand only when a clinic asks, and only after the last one can run without us.',
  'geo.p3.title': 'Wherever this leads',
  'geo.p3.body': 'Geography shouldn’t decide a health outcome. The map grows when the work is ready.',

  'gov.kicker': 'Governance',
  'gov.title': 'Nobody on our Board makes a decision about a community without that community in the room.',
  'gov.lede':
    'That’s not a slogan — it’s how we vote. Our Community Advisory Council is consulted before any program decision goes to a vote, and at least one of them sits in on every Board meeting.',
  'gov.cta': 'See how we’re organized',
  'gov.note': 'Community Advisory Council — non-voting. Always in the room.',
  'gov.board.section': '01 — Board of Directors',
  'gov.board.title': 'Seven seats, one shared fiduciary duty.',
  'gov.cac.section': '02 — Community Advisory Council',
  'gov.cac.title': 'Non-voting. Always in the room.',
  'gov.cac.body':
    'Representatives from Native American communities in our U.S. operational regions, and Malian and West African community leaders and local health workers. The Board seeks and documents their input before approving any program decision.',
  'gov.committees.section': '03 — Advisory committees',
  'gov.committees.title': 'Two more checks before anything ships.',
  'gov.med.title': 'Medical & Clinical Advisory Committee',
  'gov.med.body':
    'Licensed physicians, public health experts, and clinical ethicists review every tool and protocol before it reaches a patient.',
  'gov.tech.title': 'Technology & Data Compliance Advisory Committee',
  'gov.tech.body':
    'Digital health engineers, data privacy attorneys, and cybersecurity specialists sign off on every platform we deploy.',

  'story.p1':
    'HealthGeek started as a conversation between a computer scientist and a practicing dentist about why the same broken supply chain kept showing up in two places that had never spoken to each other — a rural clinic in Mali, and a tribal health center in Upper Michigan.',
  'story.p2':
    'Neither place needed to be rescued. They needed working equipment, someone to train the person using it, and a say in what got built next. So that’s what we built: a small non-profit that moves technology and training where they’re asked for, and hands ownership to the people who’ll use them long after we’ve left the room.',
  'story.p3': 'We’re still small. We think that’s the point — we’d rather get one clinic exactly right than promise a hundred.',
  'story.vision.section': '01 — Our vision',
  'story.vision.title': 'A world where geography doesn’t decide your health outcome.',
  'story.vision.body':
    'From Mali to Michigan and wherever this leads next, we’re working toward one plain idea: that every person, regardless of where they were born or what they can afford, gets access to healthcare that actually works for them — delivered by professionals who are supported, not stretched thin.',
  'story.vision.quote':
    'There’s a lot the global south and north have to learn from each other. We don’t think the flow of knowledge only goes one way.',
  'story.rules.section': '02 — What we won’t do',
  'story.rules.title': 'Two rules that shape everything else.',

  'trust.label': 'People we work alongside',

  'cta.title': 'The goal isn’t to be needed forever. It’s to be needed less every year.',
  'cta.lede': 'There’s a place for you in this, whoever you are.',
  'cta.donate': 'Donate to HealthGeek',
  'cta.geek': 'Become a HealthGeek Geek',
  'cta.fine1': 'A one-time gift goes straight to a clinic or a training cohort.',
  'cta.fine2': 'Recurring support lets us plan a training calendar instead of chasing one.',

  'contact.kicker': 'Get in touch',
  'contact.title': 'There’s a place for you in this, whoever you are.',
  'contact.lede': 'Reach us directly, or pick the way you’d like to help below.',
  'contact.ways.section': '01 — Ways to help',
  'contact.ways.title': 'Not everyone can write a check.',
  'contact.form.section': '02 — Send us a message',
  'contact.form.title': 'We read every one of these ourselves.',
  'contact.form.name': 'Name',
  'contact.form.email': 'Email',
  'contact.form.about': 'I’m reaching out about',
  'contact.form.country': 'Country',
  'contact.form.message': 'Message',
  'contact.form.send': 'Send message',
  'contact.form.opt.general': 'General inquiry',
  'contact.form.opt.donate': 'Donating',
  'contact.form.opt.volunteer': 'Volunteering — HealthGeek Geeks',
  'contact.form.opt.partner': 'Partnering with HealthGeek',
  'contact.form.opt.juvo': 'Juvo — equipment or training',
  'contact.form.opt.press': 'Press',
  'contact.direct.section': '03 — Direct lines',
  'contact.email': 'EMAIL',
  'contact.mali': 'MALI — OPERATIONAL HQ',
  'contact.mali.place': 'Faso Kanu, Bamako, Mali',
  'contact.us': 'UNITED STATES — ADMINISTRATIVE OFFICE',
  'contact.us.place': 'Maryland, US',

  'footer.blurb':
    'A small non-profit working in Mali and the United States, run by people who’d rather fix one thing well than announce a hundred.',
  'footer.org': 'About',
  'footer.programs': 'Programs',
  'footer.touch': 'Contact',
  'footer.volunteer': 'Get in touch',
  'footer.partner': 'Partner with us',
  'footer.copy': '© 2026 HealthGeek Initiative. healthgeek.org.ml',
  'footer.legal': 'Privacy · Safeguarding Policy · Form 990',
  'footer.mission': 'Our Story',
  'footer.reports': 'Financials & Form 990',

  'page.mission.title': 'Two people, one question.',
  'page.mission.lede':
    'A conversation between a computer scientist and a practicing dentist about a supply chain that kept breaking in two places that had never spoken.',
  'page.programs.title': 'Programs',
  'page.learning.title': 'Learning',
  'page.learning.lede':
    'Training for the people who keep clinics running — maintenance, digital workflows, and community health practice.',
  'page.impact.title': 'By the numbers.',
  'page.governance.title': 'Governance',
  'page.where.title': 'Where we work',
  'page.involved.title': 'Ways to help',
  'page.donate.title': 'Make a donation',
  'page.contact.title': 'Contact',
  'page.community.title': 'Community',
  'page.partners.title': 'Partners',
  'page.news.title': 'News',
  'page.transparency.title': 'Financials & Form 990',
  'page.privacy.title': 'Privacy',
  'page.terms.title': 'Terms',
  'page.safeguarding.title': 'Safeguarding',
  'page.notfound.title': 'Page not found',
  'page.notfound.body': 'That path isn’t part of this site.',
  'page.notfound.cta': 'Back home',
} as const;

export type MessageKey = keyof typeof en;

const fr: Record<MessageKey, string> = {
  'nav.mission': 'Notre histoire',
  'nav.programs': 'Programmes',
  'nav.learning': 'Formation',
  'nav.impact': 'Impact',
  'nav.where': 'Où nous travaillons',
  'nav.governance': 'Gouvernance',
  'nav.community': 'Communauté',
  'nav.partners': 'Partenaires',
  'nav.news': 'Actualités',
  'nav.involved': "S'engager",
  'nav.donate': 'Faire un don',
  'nav.contact': 'Contact',
  'nav.home': 'Accueil',
  'nav.more': 'Plus',
  'nav.menu': 'Ouvrir le menu',
  'nav.close': 'Fermer le menu',
  'lang.en': 'EN',
  'lang.fr': 'FR',
  'lang.bm': 'BM',

  'doc.org': 'HEALTHGEEK INITIATIVE — 501(c)(3)',
  'brand.health': 'HEALTH',
  'brand.geek': 'GEEK',

  'hero.pill': 'Est. 2026 — Mali & États-Unis',
  'hero.title.1': 'Deux lieux,',
  'hero.title.em': 'un',
  'hero.title.2': 'réseau.',
  'hero.lede':
    'Une petite organisation au Mali et aux États-Unis, menée par des gens qui préfèrent bien réparer une chose plutôt qu’en annoncer cent.',
  'hero.quote':
    'Nous n’avons pas créé HealthGeek pour apporter des soins à quiconque. Nous l’avons créé parce qu’un dentiste à Bamako et une clinique dans une réserve du Michigan résolvent le même problème, seuls, alors qu’ils n’ont pas à l’être.',
  'hero.attr': '— Dr Miah Ramsey, présidente et directrice générale',
  'hero.cta.programs': 'Voir nos programmes',
  'hero.cta.charter': 'Lire notre histoire',
  'hero.illus.caption': 'Bamako et le Haut-Michigan — un réseau, deux points de départ.',
  'hero.stat.clinics': 'Cliniques partenaires et centres de santé communautaires',
  'hero.stat.countries': 'Pays — Mali et États-Unis — un seul réseau',
  'hero.stat.codesign': 'Appareils reconditionnés et redéployés',
  'hero.video.kicker': 'Sur le terrain',
  'hero.video.title': 'Bamako et le Haut-Michigan, dans la même pièce.',

  'impact.kicker': '01 — Où nous en sommes',
  'impact.lede': 'Mis à jour chaque trimestre. Le détail figure dans notre rapport annuel.',
  'impact.title': 'En chiffres.',
  'impact.clinics.label': 'Cliniques partenaires et centres de santé communautaires',
  'impact.clinics.desc': 'Cliniques et centres actuellement dans le réseau.',
  'impact.chws.label': 'Soignants formés par nos programmes',
  'impact.chws.desc': 'Cliniciens, techniciens et agents de santé formés à ce jour.',
  'impact.regions.label': 'Pays — Mali et États-Unis — un seul réseau',
  'impact.regions.desc': 'Deux points de départ, une même approche.',
  'impact.local.label': 'Appareils reconditionnés et redéployés',
  'impact.local.desc': 'Matériel remis en service grâce à Juvo.',

  'pillars.kicker': 'Notre histoire',
  'pillars.title': 'Deux personnes, une question.',
  'pillars.lede':
    'HealthGeek est né d’une conversation entre un informaticien et une dentiste sur une chaîne d’approvisionnement qui cassait au même endroit, dans deux lieux qui ne s’étaient jamais parlé.',
  'pillars.1.n': 'RÈGLE UN',
  'pillars.1.title': 'Nous ne concevons rien sans les gens qui s’en serviront.',
  'pillars.1.body':
    'Chaque intervention est co-créée avec les leaders locaux. Les communautés décident de ce qui se construit ensuite, et gardent ce qui marche après notre départ.',
  'pillars.2.n': 'RÈGLE DEUX',
  'pillars.2.title': 'Nous ne cherchons pas à être indispensables pour toujours.',
  'pillars.2.body':
    'Nous nous mesurons à la capacité transmise, pas à l’aide livrée. Si une clinique n’a plus besoin de nous dans cinq ans, nous avons fait notre travail.',
  'pillars.3.n': 'VISION',
  'pillars.3.title': 'Un monde où la géographie ne décide pas de votre santé.',
  'pillars.3.body':
    'Du Mali au Michigan, chacun devrait recevoir des soins qui fonctionnent vraiment — délivrés par des professionnels soutenus, pas épuisés.',
  'pillars.4.n': 'ÉCHANGE',
  'pillars.4.title': 'Le savoir ne circule pas dans un seul sens.',
  'pillars.4.body':
    'Le Sud et le Nord ont beaucoup à apprendre l’un de l’autre. Nous construisons pour que l’échange aille dans les deux directions.',

  'programs.kicker': 'Programmes',
  'programs.title': 'Trois programmes, une mission.',
  'programs.lede': 'Chacun est mené par les gens les plus proches du travail.',
  'programs.mali.tag': 'HealthGeek Mali',
  'programs.mali.title': 'Réparer d’abord ce qui est déjà là.',
  'programs.mali.body':
    'Le premier projet à Bamako n’était pas du matériel neuf — c’était une relation avec des cliniques dont les machines étaient déjà en panne dans une pièce du fond. Tout ce qui a suivi a obéi à la même règle : réparer ce qui est là avant d’envoyer du neuf.',
  'programs.mali.teaser': 'Un appui clinique mené par les communautés, autour de la maintenance et de la formation.',
  'programs.mali.link': 'Lire notre approche',
  'programs.juvo.tag': 'Juvo',
  'programs.juvo.title': 'Reconditionner, former, redéployer.',
  'programs.juvo.body':
    'Juvo reçoit le matériel médical donné ou mis au rebut, et rend aux cliniques de deux façons : de l’équipement réparé, ou un crédit pour une formation qui apprend à réparer le suivant.',
  'programs.juvo.teaser': 'Un modèle circulaire qui transforme le matériel donné en cliniques qui marchent.',
  'programs.juvo.link': 'Demander du matériel ou une formation',
  'programs.di.tag': 'Dental Intelligence',
  'programs.di.title': 'Le spécialiste le plus proche, dans la pièce.',
  'programs.di.body':
    'Dental Intelligence lit une radio au moment où elle est prise, signale ce qu’un œil formé pourrait manquer, et s’améliore à chaque usage — pour que l’expert le plus proche soit l’outil dans la pièce, pas un trajet de quatre heures.',
  'programs.di.teaser': 'Relecture radiographique assistée par l’IA et formation clinique, conçues pour la pratique rurale.',
  'programs.di.link': 'Ouvrir dental.healthgeek.org.ml',

  'programs.section': '02 — Ce que nous construisons',
  'programs.page.title': 'Trois efforts, racontés comme les gens qui les mènent vous les diraient.',
  'programs.mali.headline': 'Seb ne voulait plus d’un appareil de radio donné que personne ne peut réparer.',
  'programs.mali.who': 'Seb Diarra, secrétaire du conseil et directeur des programmes, Mali',
  'programs.mali.meta1': 'Bamako',
  'programs.mali.meta1l': 'Siège opérationnel',
  'programs.mali.meta2': 'Phase 1',
  'programs.mali.meta2l': 'Expansion en Afrique de l’Ouest',
  'programs.juvo.headline': 'Le matériel dont personne ne voulait est devenu la formation que personne ne pouvait payer.',
  'programs.juvo.who': 'Équipe fondatrice : un informaticien et une dentiste',
  'programs.juvo.meta1': '30+',
  'programs.juvo.meta1l': 'Appareils reconditionnés',
  'programs.juvo.meta2': '8',
  'programs.juvo.meta2l': 'Programmes de certification',
  'programs.di.headline': 'Un étudiant en dentaire dans un village ne devrait pas avoir besoin d’un spécialiste au téléphone.',
  'programs.di.who': 'Développé avec l’Ordre national des chirurgiens-dentistes du Mali et RobotsMali AI4D Lab',
  'programs.di.meta1': 'Assisté par l’IA',
  'programs.di.meta1l': 'Relecture radiographique',
  'programs.di.meta2': 'Certifié',
  'programs.di.meta2l': 'Suivi des compétences et certification',

  'help.section': '03 — Comment aider',
  'help.title': 'Il y a une place pour vous ici, qui que vous soyez.',
  'help.lede': 'Tout le monde ne peut pas écrire un chèque, et tout le monde ne le veut pas. Voici ce qui fait vraiment avancer les choses.',
  'help.donate.title': 'Faire un don',
  'help.donate.body': 'Un don unique va directement à une clinique ou une cohorte de formation — vous saurez laquelle.',
  'help.monthly.title': 'Donner chaque mois',
  'help.monthly.body': 'Le soutien récurrent permet de planifier un calendrier de formation au lieu de le courir.',
  'help.geek.title': 'Devenir un HealthGeek Geek',
  'help.geek.body': 'Experts bénévoles, fellows et volontaires locaux qui font le travail sur le terrain.',
  'help.partner.title': 'Devenir partenaire',
  'help.partner.body': 'Entreprises de health-tech, universités et équipes RSE — écrivez-nous, on commencera la conversation.',

  'geo.title.1': 'Un réseau,',
  'geo.title.em': 'deux',
  'geo.title.2': 'points de départ.',
  'geo.cap1': 'Bamako et le Haut-Michigan',
  'geo.cap2': 'Un réseau, deux points de départ.',
  'geo.us.title': 'États-Unis',
  'geo.us.body':
    'Communautés amérindiennes et centres de santé tribaux, à commencer par le Haut-Michigan, plus d’autres cliniques rurales sous-desservies.',
  'geo.p1.title': 'Mali — siège opérationnel',
  'geo.p1.body': 'Appui clinique mené par les communautés depuis Bamako, autour de la maintenance et de la formation.',
  'geo.p2.title': 'Afrique de l’Ouest, ensuite',
  'geo.p2.body': 'Nous n’étendons que lorsqu’une clinique le demande, et seulement quand la précédente peut tourner sans nous.',
  'geo.p3.title': 'Là où cela mène',
  'geo.p3.body': 'La géographie ne devrait pas décider d’un résultat de santé. La carte grandit quand le travail est prêt.',

  'gov.kicker': 'Gouvernance',
  'gov.title': 'Personne au Conseil ne décide pour une communauté sans cette communauté dans la pièce.',
  'gov.lede':
    'Ce n’est pas un slogan — c’est la règle de vote. Notre conseil consultatif communautaire est consulté avant tout vote programmatique, et l’un d’eux assiste à chaque réunion du Conseil.',
  'gov.cta': 'Voir comment nous sommes organisés',
  'gov.note': 'Conseil consultatif communautaire — sans droit de vote. Toujours dans la pièce.',
  'gov.board.section': '01 — Conseil d’administration',
  'gov.board.title': 'Sept sièges, un devoir fiduciaire partagé.',
  'gov.cac.section': '02 — Conseil consultatif communautaire',
  'gov.cac.title': 'Sans droit de vote. Toujours dans la pièce.',
  'gov.cac.body':
    'Représentants des communautés amérindiennes dans nos régions américaines, et leaders maliens et ouest-africains ainsi que soignants locaux. Le Conseil recueille et documente leur avis avant d’approuver toute décision de programme.',
  'gov.committees.section': '03 — Comités consultatifs',
  'gov.committees.title': 'Deux contrôles de plus avant que quoi que ce soit parte.',
  'gov.med.title': 'Comité consultatif médical et clinique',
  'gov.med.body':
    'Médecins, experts de santé publique et éthiciens cliniques relisent chaque outil et protocole avant qu’il n’atteigne un patient.',
  'gov.tech.title': 'Comité consultatif technologie et conformité des données',
  'gov.tech.body':
    'Ingénieurs en santé numérique, juristes de la vie privée et spécialistes de la cybersécurité valident chaque plateforme déployée.',

  'story.p1':
    'HealthGeek est né d’une conversation entre un informaticien et une dentiste sur une chaîne d’approvisionnement qui cassait au même endroit, dans deux lieux qui ne s’étaient jamais parlé — une clinique rurale au Mali, et un centre de santé tribal dans le Haut-Michigan.',
  'story.p2':
    'Aucun de ces lieux n’avait besoin d’être sauvé. Ils avaient besoin de matériel qui marche, de quelqu’un pour former la personne qui s’en sert, et d’un mot à dire sur ce qui se construit ensuite. C’est ce que nous avons bâti : une petite organisation qui déplace technologie et formation là où on les demande, et remet la propriété aux gens qui s’en serviront longtemps après notre départ.',
  'story.p3': 'Nous sommes encore petits. C’est le point — nous préférons réussir une clinique exactement plutôt que d’en promettre cent.',
  'story.vision.section': '01 — Notre vision',
  'story.vision.title': 'Un monde où la géographie ne décide pas de votre santé.',
  'story.vision.body':
    'Du Mali au Michigan et partout où cela mènera, une idée simple : chacun, où qu’il soit né et quoi qu’il puisse payer, accède à des soins qui fonctionnent vraiment — délivrés par des professionnels soutenus, pas épuisés.',
  'story.vision.quote':
    'Le Sud et le Nord ont beaucoup à apprendre l’un de l’autre. Nous ne pensons pas que le savoir ne circule que dans un sens.',
  'story.rules.section': '02 — Ce que nous ne ferons pas',
  'story.rules.title': 'Deux règles qui façonnent tout le reste.',

  'trust.label': 'Les gens avec qui nous travaillons',

  'cta.title': 'Le but n’est pas d’être indispensable pour toujours. C’est d’être un peu moins nécessaire chaque année.',
  'cta.lede': 'Il y a une place pour vous ici, qui que vous soyez.',
  'cta.donate': 'Faire un don à HealthGeek',
  'cta.geek': 'Devenir un HealthGeek Geek',
  'cta.fine1': 'Un don unique va directement à une clinique ou une cohorte de formation.',
  'cta.fine2': 'Le soutien récurrent permet de planifier un calendrier de formation.',

  'contact.kicker': 'Nous écrire',
  'contact.title': 'Il y a une place pour vous ici, qui que vous soyez.',
  'contact.lede': 'Écrivez-nous directement, ou choisissez ci-dessous comment vous voulez aider.',
  'contact.ways.section': '01 — Comment aider',
  'contact.ways.title': 'Tout le monde ne peut pas écrire un chèque.',
  'contact.form.section': '02 — Envoyer un message',
  'contact.form.title': 'Nous lisons chacun de ces messages nous-mêmes.',
  'contact.form.name': 'Nom',
  'contact.form.email': 'E-mail',
  'contact.form.about': 'Je vous contacte pour',
  'contact.form.country': 'Pays',
  'contact.form.message': 'Message',
  'contact.form.send': 'Envoyer',
  'contact.form.opt.general': 'Question générale',
  'contact.form.opt.donate': 'Un don',
  'contact.form.opt.volunteer': 'Bénévolat — HealthGeek Geeks',
  'contact.form.opt.partner': 'Partenariat avec HealthGeek',
  'contact.form.opt.juvo': 'Juvo — matériel ou formation',
  'contact.form.opt.press': 'Presse',
  'contact.direct.section': '03 — Lignes directes',
  'contact.email': 'E-MAIL',
  'contact.mali': 'MALI — SIÈGE OPÉRATIONNEL',
  'contact.mali.place': 'Faso Kanu, Bamako, Mali',
  'contact.us': 'ÉTATS-UNIS — BUREAU ADMINISTRATIF',
  'contact.us.place': 'Maryland, États-Unis',

  'footer.blurb':
    'Une petite organisation au Mali et aux États-Unis, menée par des gens qui préfèrent bien réparer une chose plutôt qu’en annoncer cent.',
  'footer.org': 'À propos',
  'footer.programs': 'Programmes',
  'footer.touch': 'Contact',
  'footer.volunteer': 'Nous écrire',
  'footer.partner': 'Devenir partenaire',
  'footer.copy': '© 2026 HealthGeek Initiative. healthgeek.org.ml',
  'footer.legal': 'Confidentialité · Protection · Formulaire 990',
  'footer.mission': 'Notre histoire',
  'footer.reports': 'Finances et formulaire 990',

  'page.mission.title': 'Deux personnes, une question.',
  'page.mission.lede':
    'Une conversation entre un informaticien et une dentiste sur une chaîne d’approvisionnement qui cassait dans deux lieux qui ne s’étaient jamais parlé.',
  'page.programs.title': 'Programmes',
  'page.learning.title': 'Formation',
  'page.learning.lede':
    'Des formations pour les gens qui font tourner les cliniques — maintenance, flux numériques, et pratique de santé communautaire.',
  'page.impact.title': 'En chiffres.',
  'page.governance.title': 'Gouvernance',
  'page.where.title': 'Où nous travaillons',
  'page.involved.title': 'Comment aider',
  'page.donate.title': 'Faire un don',
  'page.contact.title': 'Contact',
  'page.community.title': 'Communauté',
  'page.partners.title': 'Partenaires',
  'page.news.title': 'Actualités',
  'page.transparency.title': 'Finances et formulaire 990',
  'page.privacy.title': 'Confidentialité',
  'page.terms.title': 'Conditions',
  'page.safeguarding.title': 'Protection',
  'page.notfound.title': 'Page introuvable',
  'page.notfound.body': 'Ce chemin ne fait pas partie de ce site.',
  'page.notfound.cta': "Retour à l'accueil",
};

const bm: Record<MessageKey, string> = {
  ...en,
  'nav.mission': 'An ka tariku',
  'nav.programs': 'Baarakɛw',
  'nav.learning': 'Kalanni',
  'nav.impact': 'Njɛ',
  'nav.where': 'Yɔrɔ minnu na',
  'nav.governance': 'Ɲɛmɔgɔya',
  'nav.community': 'Jɛkulu',
  'nav.partners': 'Jɛɲɔgɔnw',
  'nav.news': 'Kibaaru',
  'nav.involved': 'Kɛlɛn',
  'nav.donate': 'Sɔrɔkɔ',
  'nav.contact': 'Wele',
  'nav.home': 'So',
  'nav.more': 'Wɛrɛ',
  'nav.menu': 'Menu yɛlɛ',
  'nav.close': 'Menu datugulen',
  'hero.cta.programs': 'Baarakɛw lajɛ',
  'hero.cta.charter': 'An ka tariku kalan',
  'cta.donate': 'Sɔrɔkɔ HealthGeek ma',
  'page.notfound.cta': 'So segin',
};

export const messages: Record<Locale, Record<MessageKey, string>> = { en, fr, bm };
